import React, { useEffect, useState } from 'react'
import { Category_ } from 'src/api/db/apiCategory'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Button } from 'src/shared/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'src/shared/components/ui/sheet'
import { Input } from 'src/shared/components/ui/input'
import { Textarea } from 'src/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shared/components/ui/select'
import { useToast } from 'src/shared/hooks/use-toast'

interface AddEditCategoryDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (formData: {
    title: string
    description?: string
    color?: string
    image_url?: string
    parent_category_id?: number | null
  }) => Promise<void>
  category: Category_ | null
  allCategories: Category_[]
}

export const AddEditCategoryDialog = ({
  isOpen,
  onClose,
  onSave,
  category,
  allCategories,
}: AddEditCategoryDialogProps) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [parentCategoryId, setParentCategoryId] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = !!category

  // Reset form when dialog opens/closes or category changes
  useEffect(() => {
    if (isOpen) {
      if (category) {
        setTitle(category.title || '')
        setDescription(category.description || '')
        setColor(category.color || '')
        setImageUrl(category.image_url || '')
        setParentCategoryId(category.parent_category_id?.toString() || '')
      } else {
        setTitle('')
        setDescription('')
        setColor('')
        setImageUrl('')
        setParentCategoryId('')
      }
    }
  }, [isOpen, category])

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: t('Title is required', 'Titel krävs'),
      })
      return
    }

    setIsSubmitting(true)
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        color: color.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        parent_category_id:
          parentCategoryId === '' || parentCategoryId === 'none'
            ? null
            : parseInt(parentCategoryId),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault()
      handleSave()
    }
  }

  // Filter out the current category from parent options (to prevent circular references)
  const availableParentCategories = allCategories.filter(
    (cat) => !category || cat.id !== category.id
  )

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEditMode
              ? t('Edit Category', 'Redigera kategori')
              : t('Add New Category', 'Lägg till ny kategori')}
          </SheetTitle>
          <SheetDescription>
            {isEditMode
              ? t(
                  'Update the category information.',
                  'Uppdatera kategorinformationen.'
                )
              : t(
                  'Create a new category to organize products.',
                  'Skapa en ny kategori för att organisera produkter.'
                )}
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-4">
          <Input
            label={`${t('Title', 'Titel')} *`}
            placeholder={t('Enter category title', 'Ange kategorititel')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />

          <Textarea
            label={`${t('Description', 'Beskrivning')} (${t('Optional', 'Valfritt')})`}
            placeholder={t(
              'Enter category description',
              'Ange kategoribeskrivning'
            )}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex gap-2 items-center">
              <Input
                type="color"
                value={color || '#000000'}
                onChange={(e) => setColor(e.target.value)}
                className="w-16 h-10 p-1"
              />
              <Input
                label={`${t('Color', 'Färg')} (${t('Optional', 'Valfritt')})`}
                placeholder={t('Hex color code', 'Hex-färgkod')}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <Input
              label={`${t('Image URL', 'Bild-URL')} (${t('Optional', 'Valfritt')})`}
              placeholder={t('Enter image URL', 'Ange bild-URL')}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <Select
            label={`${t('Parent Category', 'Överordnad kategori')} (${t('Optional', 'Valfritt')})`}
            value={parentCategoryId}
            onValueChange={setParentCategoryId}
          >
              <SelectTrigger>
                <SelectValue
                  placeholder={t(
                    'Select parent category',
                    'Välj överordnad kategori'
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('None', 'Ingen')}</SelectItem>
                {availableParentCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.title || `Category ${cat.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
        </div>

        <SheetFooter>
          <Button onClick={onClose} disabled={isSubmitting} variant="secondary">
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !title.trim()}
            variant="primary"
          >
            {isSubmitting
              ? isEditMode
                ? t('Updating...', 'Uppdaterar...')
                : t('Creating...', 'Skapar...')
              : isEditMode
              ? t('Update Category', 'Uppdatera kategori')
              : t('Create Category', 'Skapa kategori')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
